import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { adminLogin } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9e7963b6-8d0a-418d-8246-af7a5b4f8387/artifacts/h6dgewwm_image.png';

export const AdminLoginPage = () => {
  const { login, isAdmin, t } = useApp();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAdmin) {
      navigate('/admin');
    }
  }, [isAdmin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin({ email: form.email, password: form.password });
      login(res.data.access_token, res.data.admin);
      toast.success(t('adminAuth.loginSuccess'));
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('adminAuth.authFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="admin-login-page">
      <Toaster position="top-right" theme="dark" />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <img src={LOGO_URL} alt="LUXOR TEK" className="h-12 mx-auto mb-6" />
          <h1 className="text-2xl font-bold">{t('adminAuth.portal')}</h1>
          <p className="text-white/60 mt-2">
            {t('adminAuth.signInManage')}
          </p>
        </div>

        {/* Form */}
        <div className="card-dark p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminAuth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-12 pr-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                  placeholder="admin@luxortek.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminAuth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit-btn"
            >
              {loading ? t('adminAuth.pleaseWait') : t('adminAuth.signIn')}
            </button>
          </form>
        </div>

        {/* Back to site */}
        <div className="mt-8 text-center">
          <a href="/" className="text-white/40 hover:text-white text-sm">
            &larr; {t('adminAuth.backToWebsite')}
          </a>
        </div>
      </div>
    </div>
  );
};
